import { Box } from '@mui/material';
import { TIMING } from '@src/constants/timing';
import { AnimatePresence, motion } from 'framer-motion';
import { memo, type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

/**
 * フォーカスモード用画像コンポーネントのProps型定義
 */
type FocusModeImageProps = {
  isVisible?: boolean;
};

/**
 * フォーカスモード用画像コンポーネント
 *
 * 落ち着いた風景画像を表示し、入力に集中できる環境を提供します。
 * 複数の画像を自動的に切り替えて表示します。
 * 画像切り替え時は美しいクロスフェードエフェクトで、現在の画像が徐々に暗くなりながら
 * 次の画像が徐々に明るくなって表示されます。
 * framer-motionとreact-intersection-observerを使用して、洗練されたアニメーションと
 * 効率的な画像管理を実現します。
 *
 * @param {FocusModeImageProps} props - コンポーネントのプロパティ
 * @param {boolean} [props.isVisible] - 表示状態
 * @returns {ReactElement} フォーカスモード用画像コンポーネント
 */
const FocusModeImage = memo(({ isVisible = true }: FocusModeImageProps): ReactElement | null => {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [imagesLoaded, setImagesLoaded] = useState<Set<number>>(new Set([0, 1]));
  const [isNextImageReady, setIsNextImageReady] = useState(false);
  const [isTextVisible, setIsTextVisible] = useState(false);

  // 画像のプリロード用ref
  const imageRefs = useRef<{ [key: number]: HTMLImageElement | null }>({});

  // Intersection Observer用のref
  const { ref: containerRef, inView } = useInView({
    threshold: 0.1,
    triggerOnce: false,
  });

  // フォーカスモード用の落ち着いた風景画像（ローカルファイル）
  const focusImages = [
    { src: '/images/focus-mode/coast-sunset.jpg', alt: '海岸の夕日' },
    { src: '/images/focus-mode/forest-path.jpg', alt: '森の小径' },
    { src: '/images/focus-mode/mountain-forest.jpg', alt: '山の森' },
    { src: '/images/focus-mode/night-city.jpg', alt: '夜の街' },
  ];

  // 画像のプリロード処理
  const preloadImage = useCallback((index: number): Promise<void> => {
    return new Promise((resolve, reject) => {
      if (imageRefs.current[index]) {
        resolve();
        return;
      }

      const img = new Image();
      img.onload = () => {
        imageRefs.current[index] = img;
        setImagesLoaded((prev) => new Set([...prev, index]));
        resolve();
      };
      img.onerror = () => {
        reject(new Error(`Failed to load image ${index}`));
      };
      img.src = focusImages[index].src;
    });
  }, []);

  // 次の画像の準備
  const prepareNextImage = useCallback(async () => {
    const nextIndex = (currentImageIndex + 1) % focusImages.length;

    try {
      await preloadImage(nextIndex);
      setIsNextImageReady(true);
    } catch {
      // Failed to prepare next image
      setIsNextImageReady(false);
    }
  }, [currentImageIndex, preloadImage]);

  // 画像切り替え処理
  const switchImage = useCallback(async () => {
    if (isTransitioning || !isNextImageReady) {
      return;
    }

    setIsTransitioning(true);

    // フェードアウト開始
    await new Promise((resolve) => setTimeout(resolve, TIMING.IMAGE_TRANSITION_DURATION));

    // 画像インデックス更新
    setCurrentImageIndex((prev) => (prev + 1) % focusImages.length);
    setIsNextImageReady(false);

    // フェードイン完了
    await new Promise((resolve) => setTimeout(resolve, TIMING.IMAGE_TRANSITION_DURATION));
    setIsTransitioning(false);

    // 次の画像を準備
    prepareNextImage();
  }, [isTransitioning, isNextImageReady, prepareNextImage]);

  // 自動画像切り替えの設定
  useEffect(() => {
    if (!isVisible || !inView) {
      return;
    }

    const interval = setInterval(() => {
      if (isNextImageReady && !isTransitioning) {
        switchImage();
      }
    }, TIMING.IMAGE_AUTO_SWITCH_INTERVAL); // 15秒

    return () => clearInterval(interval);
  }, [isVisible, inView, isNextImageReady, isTransitioning, switchImage]);

  // 初期画像の準備
  useEffect(() => {
    if (isVisible) {
      // 最初の2枚の画像をプリロード
      Promise.all([preloadImage(0), preloadImage(1)])
        .then(() => {
          setIsNextImageReady(true);
        })
        .catch(() => {});
    }
  }, [isVisible, preloadImage]);

  // フォーカスモード表示時のエフェクト制御
  useEffect(() => {
    if (isVisible) {
      // 背景が完全に表示された後にテキストを表示
      const textTimer = setTimeout(() => {
        setIsTextVisible(true);
      }, TIMING.FOCUS_MODE_TEXT_DELAY); // 800ms後にテキスト表示開始

      return () => clearTimeout(textTimer);
    } else {
      // フォーカスモード終了時は即座にリセット
      setIsTextVisible(false);
    }
  }, [isVisible]);

  // 手動切り替えハンドラー
  const handleManualSwitch = useCallback(
    async (targetIndex: number) => {
      if (isTransitioning || !imagesLoaded.has(targetIndex)) {
        return;
      }

      setIsTransitioning(true);

      // フェードアウト
      await new Promise((resolve) => setTimeout(resolve, TIMING.IMAGE_MANUAL_SWITCH_DURATION));

      // 画像インデックス更新
      setCurrentImageIndex(targetIndex);
      const nextIndex = (targetIndex + 1) % focusImages.length;

      // 次の画像を準備
      try {
        await preloadImage(nextIndex);
        setIsNextImageReady(true);
      } catch (_error) {
        // Failed to prepare next image
      }

      // フェードイン完了
      await new Promise((resolve) => setTimeout(resolve, TIMING.IMAGE_MANUAL_SWITCH_DURATION));
      setIsTransitioning(false);
    },
    [isTransitioning, imagesLoaded, preloadImage]
  );

  if (!isVisible) {
    return null;
  }

  return (
    <Box
      ref={containerRef}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100vh',
        overflow: 'hidden',
      }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={currentImageIndex}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{
            opacity: 1,
            scale: 1,
          }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{
            duration: 1.2,
            ease: [0.4, 0, 0.2, 1],
          }}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
          }}
        >
          <motion.div
            initial={{ scale: 1 }}
            animate={{
              scale: [1, 1.1, 1],
              x: [0, -20, 0],
              y: [0, -10, 0],
            }}
            transition={{
              duration: 30,
              ease: 'linear',
              repeat: Infinity,
              repeatType: 'reverse',
            }}
            style={{
              width: '110%',
              height: '110%',
              position: 'relative',
              left: '-5%',
              top: '-5%',
            }}
          >
            <Box
              component="img"
              src={focusImages[currentImageIndex].src}
              alt={focusImages[currentImageIndex].alt}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'brightness(0.5)',
                transition: 'filter 0.3s ease',
                cursor: 'pointer',
              }}
              onClick={() => handleManualSwitch((currentImageIndex + 1) % focusImages.length)}
            />
          </motion.div>
        </motion.div>
      </AnimatePresence>

      {/* 画面中央付近のフォーカスモード表示 */}
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          alignItems: 'center',
          pointerEvents: 'none',
          opacity: isTextVisible ? 1 : 0,
          filter: isTextVisible ? 'blur(0px)' : 'blur(8px)',
          transition: 'all 1.5s cubic-bezier(0.4, 0, 0.2, 1)',
          zIndex: 4,
          // 微細な浮遊感を演出
          animation: isTextVisible ? 'gentleFloat 20s ease-in-out infinite' : 'none',
          '@keyframes gentleFloat': {
            '0%': {
              transform: 'translate(-50%, -50%) translateY(0px)',
            },
            '50%': {
              transform: 'translate(-50%, -50%) translateY(-5px)',
            },
            '100%': {
              transform: 'translate(-50%, -50%) translateY(0px)',
            },
          },
        }}
      >
        {/* フォーカスモードのテキスト表示は画面右上部に移動したため、ここでは非表示 */}
      </Box>
    </Box>
  );
});

FocusModeImage.displayName = 'FocusModeImage';

export default FocusModeImage;
